/**
 * IndexedDB Storage Provider для Supabase Auth
 *
 * Проблема: localStorage в iOS PWA очищается через 7 дней неиспользования
 * Решение: IndexedDB сохраняется дольше и надежнее в PWA
 */

const DB_NAME = "MGFoodAuth";
const DB_VERSION = 1;
const STORE_NAME = "auth";

let db = null;

const openDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
  });
};

const getItem = async (key) => {
  try {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        if (request.result !== undefined) {
          resolve(request.result);
        } else {
          // Fallback: try localStorage backup
          try {
            const backup = localStorage.getItem(`idb_backup_${key}`);
            resolve(backup || null);
          } catch (lsError) {
            resolve(null);
          }
        }
      };

      request.onerror = () => {
        // Fallback to localStorage
        try {
          const backup = localStorage.getItem(`idb_backup_${key}`);
          resolve(backup || null);
        } catch (lsError) {
          resolve(null);
        }
      };
    });
  } catch (error) {
    // Fallback to localStorage
    try {
      const backup = localStorage.getItem(`idb_backup_${key}`);
      return backup || null;
    } catch (lsError) {
      return null;
    }
  }
};

const setItem = async (key, value) => {
  try {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(value, key);

      request.onsuccess = () => {
        // Дополнительный backup в localStorage
        try {
          localStorage.setItem(`idb_backup_${key}`, value);
        } catch (lsError) {
          // Игнорируем ошибки localStorage (quota exceeded и т.д.)
        }
        resolve();
      };

      request.onerror = () => {
        // Fallback: сохраняем в localStorage
        try {
          localStorage.setItem(`idb_backup_${key}`, value);
          resolve(); // Считаем успехом если хотя бы в localStorage сохранили
        } catch (lsError) {
          reject(request.error);
        }
      };
    });
  } catch (error) {
    // Fallback to localStorage
    try {
      localStorage.setItem(`idb_backup_${key}`, value);
    } catch (lsError) {
      // Игнорируем
    }
  }
};

const removeItem = async (key) => {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const transaction = database.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => {
        try {
          localStorage.removeItem(`idb_backup_${key}`);
        } catch (e) {
          // Ignore localStorage errors
        }
        resolve();
      };

      request.onerror = () => {
        // Fallback: пытаемся удалить из localStorage
        try {
          localStorage.removeItem(`idb_backup_${key}`);
        } catch (e) {
          // Ignore
        }
        // Не отклоняем промис - удаление всё равно считаем успешным
        resolve();
      };
    });
  } catch (error) {
    // Fail-safe: если IndexedDB недоступен, пробуем localStorage
    try {
      localStorage.removeItem(`idb_backup_${key}`);
    } catch (e) {
      // Ignore
    }
    // Всё равно не падаем - auth cleanup не должен ломать вход
  }
};

const clear = async () => {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const transaction = database.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        try {
          Object.keys(localStorage).forEach((k) => {
            if (k.startsWith("idb_backup_")) {
              localStorage.removeItem(k);
            }
          });
        } catch (e) {
          // Ignore localStorage errors
        }
        resolve();
      };

      request.onerror = () => {
        // Fallback: очищаем localStorage backup
        try {
          Object.keys(localStorage).forEach((k) => {
            if (k.startsWith("idb_backup_")) {
              localStorage.removeItem(k);
            }
          });
        } catch (e) {
          // Ignore
        }
        // Не отклоняем промис - очистка не должна ломать вход
        resolve();
      };
    });
  } catch (error) {
    // Fail-safe: если IndexedDB недоступен, чистим только localStorage
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("idb_backup_")) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) {
      // Ignore
    }
  }
};

export const indexedDBStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
};

export default indexedDBStorage;
